import React, { useState, useMemo, useRef, useEffect } from 'react';
import { css, cx } from '@emotion/css';
import { useNavigate } from 'react-router-dom';
import { IoCalendarOutline, IoTimeOutline } from 'react-icons/io5';
import type { EventDetail, EventScheduleDetail } from '../../../../api/services/events/types';
import { OptionButton, OptionButtonGroup } from '../../../../shared/ui/option-button';

interface GroupedSchedule {
  date: string;
  schedules: EventScheduleDetail[];
}

const container = css({
  width: '100%',
  fontSize: 'var(--fs-400)',
  borderRadius: 'var(--spacing-sm)',
  overflow: 'hidden',
  backgroundColor: 'var(--primary-color)',
  boxShadow: 'var(--shadow-300)',
});

const header = css({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'var(--pc-500)',
  color: 'var(--primary-color)',
  fontWeight: 600,
  fontSize: 'var(--fs-450)',
  padding: 'var(--spacing-md) var(--spacing-lg)',
  gap: 'var(--spacing-xs)',

  '& svg': {
    width: 'var(--fs-450)',
    height: 'var(--fs-450)',
    color: 'var(--pc-200)',
  },
});

const content = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-lg)',
  padding: 'var(--spacing-lg)',
});

const section = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-sm)',
});

const divider = css({
  height: '1px',
  backgroundColor: 'rgba(var(--pcrgba-200), 0.5)',
  border: 'none',
  margin: 0,
});

const optionGroup = css({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: 'var(--spacing-sm)',
  padding: '0 var(--spacing-xs) var(--spacing-sm)',
});

const optionContent = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: 'var(--spacing-2xs)',
  fontSize: 'var(--fs-350)',
  width: '100%',

  '& p': {
    width: '100%',
    textAlign: 'center',
    margin: 0,
  },

  '& p:first-of-type': {
    fontWeight: 600,
    color: 'inherit',
  },

  '& p:last-of-type': {
    color: 'inherit',
    opacity: 0.9,
  },
});

const scheduleOptionButton = css({
  flex: '1 1 auto',
  minWidth: '0',
  maxWidth: '140px',

  '@media (max-width: 480px)': {
    maxWidth: '120px',
  },
});

const button = css({
  width: '100%',
  fontSize: 'var(--fs-400)',
  padding: 'var(--spacing-sm) var(--spacing-md)',
  backgroundColor: 'var(--pc-500)',
  color: 'var(--primary-color)',
  borderRadius: 'var(--spacing-sm)',
  border: 'none',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  minHeight: '48px',
  boxShadow: '0 4px 12px rgba(var(--pcrgba-500), 0.2)',

  '&:hover:not(:disabled)': {
    transform: 'translateY(-2px)',
    backgroundColor: 'var(--pc-600)',
    boxShadow: '0 6px 15px rgba(var(--pcrgba-600), 0.3)',
  },

  '&:disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
});

const noSchedulesContainer = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-md)',
  padding: 'var(--spacing-lg)',
  textAlign: 'center',
});

const badge = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--spacing-2xs)',
  backgroundColor: 'rgba(var(--pcrgba-100), 0.9)',
  color: 'var(--pc-600)',
  border: '1px solid rgba(var(--pcrgba-300), 0.5)',
  padding: 'var(--spacing-4xs) var(--spacing-xs)',
  borderRadius: '20px',
  fontSize: 'var(--fs-350)',
  fontWeight: 600,
  margin: '0 auto',

  '& svg': {
    width: '16px',
    height: '16px',
  },
});

const message = css({
  fontSize: 'var(--fs-400)',
  color: 'var(--g-700)',
  lineHeight: 1.5,
  maxWidth: '500px',
  margin: '0 auto',

  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-350)',
  },
});

const noHoursMessage = css({
  padding: 'var(--spacing-md)',
  textAlign: 'center',
  color: 'var(--g-600)',
  fontSize: 'var(--fs-400)',
  fontStyle: 'italic',
});

const stickyBottomButton = css({
  display: 'none',
  
  '@media (max-width: 62rem)': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 'var(--spacing-md)',
    paddingBottom: 'calc(var(--spacing-md) + 4rem)', // Add space for mobile navbar
    backgroundColor: 'var(--primary-color)',
    borderTop: '1px solid rgba(var(--pcrgba-200), 0.3)',
    boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.1)',
    zIndex: 45, // Below mobile navbar (50) but above other content
    transition: 'transform 0.3s ease',
  },
});

const stickyButtonHidden = css({
  transform: 'translateY(100%)',
});

const stickyButtonContent = css({
  width: '100%',
  maxWidth: '600px',
  fontSize: 'var(--fs-400)',
  padding: 'var(--spacing-sm) var(--spacing-md)',
  backgroundColor: 'var(--pc-500)',
  color: 'var(--primary-color)',
  borderRadius: 'var(--spacing-sm)',
  border: 'none',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  minHeight: '48px',
  boxShadow: '0 4px 12px rgba(var(--pcrgba-500), 0.2)',
  
  '&:hover': {
    transform: 'translateY(-2px)',
    backgroundColor: 'var(--pc-600)',
    boxShadow: '0 6px 15px rgba(var(--pcrgba-600), 0.3)',
  },
});

export interface EventTicketBuyerProps extends React.HTMLAttributes<HTMLDivElement> {
  event: EventDetail;
}

const formatDate = (dateString: string): { weekday: string; day: string; month: string } => {
  // Extract YYYY-MM-DD directly and render without forcing a timezone
  const ymd = (dateString.match(/^\d{4}-\d{2}-\d{2}/)?.[0] ?? dateString.split('T')[0] ?? dateString);
  const [, , dayStr] = ymd.split('-');
  const safeMidday = new Date(`${ymd}T12:00:00`); // midday avoids boundary flips without manual tz handling
  const weekday = safeMidday.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '');
  const month = safeMidday.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');

  return {
    weekday: weekday.charAt(0).toUpperCase() + weekday.slice(1),
    day: dayStr.padStart(2, '0'),
    month: month.charAt(0).toUpperCase() + month.slice(1),
  };
};

const formatTime = (dateString: string): string => {
  // Display the time as provided by the server, without applying timezone conversions
  const match = dateString.match(/(?:T|\s)(\d{2}):(\d{2})/);
  if (match) {
    const [, hh, mm] = match;
    return `${hh}:${mm}`;
  }
  // Fallback to Date parsing if format is unexpected
  const iso = dateString.includes(' ') ? dateString.replace(' ', 'T') : dateString;
  const date = new Date(iso);
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

export const EventTicketBuyer: React.FC<EventTicketBuyerProps> = ({ event, className, style, ...rest }) => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(null);
  const [isInView, setIsInView] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const buyButtonRef = useRef<HTMLButtonElement>(null);

  const groupedSchedules = useMemo(() => {
    if (!event.schedules || event.schedules.length === 0) return [];

    const groups = new Map<string, EventScheduleDetail[]>();
    
    event.schedules.forEach(schedule => {
      const dateOnly = (schedule.date.match(/^\d{4}-\d{2}-\d{2}/)?.[0] ?? schedule.date.split('T')[0] ?? schedule.date);
      if (!groups.has(dateOnly)) {
        groups.set(dateOnly, []);
      }
      groups.get(dateOnly)!.push(schedule);
    });

    const result: GroupedSchedule[] = [];
    groups.forEach((schedules, date) => {
      result.push({
        date,
        schedules: schedules.sort((a, b) => 
          new Date((a.date.includes(' ') ? a.date.replace(' ', 'T') : a.date)).getTime() -
          new Date((b.date.includes(' ') ? b.date.replace(' ', 'T') : b.date)).getTime()
        ),
      });
    });

    return result.sort((a, b) => 
      new Date(`${a.date}T00:00:00Z`).getTime() - new Date(`${b.date}T00:00:00Z`).getTime()
    );
  }, [event.schedules]);

  const availableSchedules = useMemo(() => {
    if (!selectedDate) return [];
    const group = groupedSchedules.find(g => g.date === selectedDate);
    return group?.schedules || [];
  }, [selectedDate, groupedSchedules]);

  useState(() => {
    if (groupedSchedules.length > 0) {
      const firstGroup = groupedSchedules[0];
      setSelectedDate(firstGroup.date);
      if (firstGroup.schedules.length > 0) {
        setSelectedScheduleId(firstGroup.schedules[0].id);
      }
    }
  });

  // Intersection Observer to track visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Check if buy button is in view
        setIsInView(entry.isIntersecting);
      },
      {
        threshold: 0.1,
        rootMargin: '-80px 0px 0px 0px', // Account for navbar/header
      }
    );

    if (buyButtonRef.current) {
      observer.observe(buyButtonRef.current);
    }

    return () => {
      if (buyButtonRef.current) {
        observer.unobserve(buyButtonRef.current);
      }
    };
  }, []);

  const scrollToTicketBuyer = () => {
    containerRef.current?.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'center' 
    });
  };

  const handleBuyTickets = () => {
    if (!selectedScheduleId) return;
    navigate(`/finalizar-compra/assentos?scheduleId=${selectedScheduleId}`);
  };

  const hasSchedules = groupedSchedules.length > 0;

  return (
    <>
      <div ref={containerRef} className={cx(container, className)} style={style} {...rest}>
        <div className={header}>
          <IoCalendarOutline />
          <p>{hasSchedules ? 'Selecione um dia e horário' : 'Datas a serem definidas'}</p>
        </div>

        {hasSchedules ? (
          <div className={content}>
            <div className={section}>
              <OptionButtonGroup 
                value={selectedDate || undefined}
                onChange={(value) => {
                  setSelectedDate(value);
                  const group = groupedSchedules.find(g => g.date === value);
                  if (group && group.schedules.length > 0) {
                    setSelectedScheduleId(group.schedules[0].id);
                  }
                }}
              >
                <div className={optionGroup}>
                  {groupedSchedules.map(group => {
                    const dateInfo = formatDate(group.date);
                    
                    return (
                      <OptionButton
                        key={group.date}
                        value={group.date}
                        className={scheduleOptionButton}
                      >
                        <div className={optionContent}>
                          <p>{dateInfo.weekday}</p>
                          <p>{dateInfo.day} {dateInfo.month}</p>
                        </div>
                      </OptionButton>
                    );
                  })}
                </div>
              </OptionButtonGroup>
            </div>

            <hr className={divider} />

            <div className={section}>
              {availableSchedules.length === 0 ? (
                <p className={noHoursMessage}>Nenhum horário disponível para esta data</p>
              ) : (
                <OptionButtonGroup
                  value={selectedScheduleId || undefined}
                  onChange={(value) => setSelectedScheduleId(value)}
                >
                  <div className={optionGroup}>
                    {availableSchedules.map(schedule => {
                      const time = formatTime(schedule.date);
                      
                      return (
                        <OptionButton
                          key={schedule.id}
                          value={schedule.id}
                          className={scheduleOptionButton}
                        >
                          <div className={optionContent}>
                            <p>{time}</p>
                          </div>
                        </OptionButton>
                      );
                    })}
                  </div>
                </OptionButtonGroup>
              )}
            </div>

            <button
              ref={buyButtonRef}
              type="button"
              className={button}
              onClick={handleBuyTickets}
              disabled={!selectedScheduleId || availableSchedules.length === 0}
            >
              Comprar Ingressos
            </button>
          </div>
        ) : (
          <div className={noSchedulesContainer}>
            <div className={badge}>
              <IoTimeOutline />
              <span>Em espera</span>
            </div>
            <p className={message}>
              Este evento ainda não possui datas e horários definidos. Entre para a lista de espera e seja notificado quando novas datas forem disponibilizadas.
            </p>
            <button type="button" className={button}>
              Entrar na lista de espera
            </button>
          </div>
        )}
      </div>

      {/* Sticky bottom button for mobile */}
      {hasSchedules && (
        <div className={cx(stickyBottomButton, isInView && stickyButtonHidden)}>
          <button
            type="button"
            className={stickyButtonContent}
            onClick={scrollToTicketBuyer}
          >
            Selecionar Ingressos
          </button>
        </div>
      )}
    </>
  );
};

export default EventTicketBuyer;
